import { useState, useEffect, useRef } from 'react'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  UploadCloud, MessageSquare, ChevronRight, CheckCircle,
  AlertTriangle, Users, Zap, FileText, TrendingUp, BarChart3,
} from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'
import { DEMO_PROJECT_ID } from '@/data/demo/preference-center/data'

interface ProjectInfo {
  project_id: string
  name: string
  status: string
}

interface UploadInfo {
  upload_id: string
  filename: string
  row_count: number
  created_at: number
}

interface ReadinessData {
  total_twins: number
  twins_validated: number
  twins_low_confidence: number
  twins_excluded: number
  mean_accuracy: number
  wilson_lower: number
  wilson_upper: number
  p90_accuracy: number
}

interface DerivedTemplate {
  template_id: string
  name: string
  rationale: string
  pre_filled_question: string
  stimuli?: string[]
}

interface SegmentData {
  segment_id: string
  name: string
  respondent_count: number
  source_column?: string
  accuracy?: number | null
}

interface RecentResult {
  id: string
  type: 'bulk_query' | 'pre_read'
  question: string
  headline: string
  status: string
  created_at: string
  twins_used?: number
  ensemble_consensus?: number
}

interface BaselineData {
  random_baseline: number
  modal_baseline: number
  echosome_accuracy: number
  lift_vs_modal: number
}

interface ResearchThreadItem {
  thread_id: string
  position: number
  question_text: string
  result_type: string
  result_snapshot: string
  created_at: number
}

export function ProjectHomePage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const highlightTemplateId = searchParams.get('template')
  const isDemo = !projectId || projectId === DEMO_PROJECT_ID || projectId === 'demo'

  const [project, setProject] = useState<ProjectInfo | null>(null)
  const [upload, setUpload] = useState<UploadInfo | null>(null)
  const [readiness, setReadiness] = useState<ReadinessData | null>(null)
  const [templates, setTemplates] = useState<DerivedTemplate[]>([])
  const [segments, setSegments] = useState<SegmentData[]>([])
  const [recentResults, setRecentResults] = useState<RecentResult[]>([])
  const [baselines, setBaselines] = useState<BaselineData | null>(null)
  const [researchThread, setResearchThread] = useState<ResearchThreadItem[]>([])
  const [loading, setLoading] = useState(!isDemo)
  const [pulseStopped, setPulseStopped] = useState(false)
  const [runningTemplateId, setRunningTemplateId] = useState<string | null>(null)
  // Tracks whether the pipeline is still building — used to trigger re-fetch when it completes
  const [refreshTick, setRefreshTick] = useState(0)

  const token = localStorage.getItem('echo_session') ?? ''
  const headers = { Authorization: `Bearer ${token}` }

  // Pulse-stop effect: ring stays permanently, pulse stops after 2 cycles (~2400ms)
  useEffect(() => {
    if (!highlightTemplateId) return
    const timer = setTimeout(() => setPulseStopped(true), 2400)
    return () => clearTimeout(timer)
  }, [highlightTemplateId])

  // Funnel event: first_template_shown — fire once when highlighted template renders
  const funnelTemplateFiredRef = useRef(false)
  useEffect(() => {
    if (!projectId || isDemo || !highlightTemplateId || templates.length === 0 || funnelTemplateFiredRef.current) return
    funnelTemplateFiredRef.current = true
    fetch(`/api/v1/projects/${projectId}/funnel`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'first_template_shown' }),
    }).catch(() => {})
  }, [templates.length, highlightTemplateId, projectId, isDemo])

  useEffect(() => {
    // D-12 audit: no demo data on real tenant routes — isDemo guard confirmed
    if (isDemo) {
      // Demo project — show a helpful redirect prompt
      setLoading(false)
      return
    }

    // Read token inside the effect so it is not a dependency (avoids re-firing
    // all seven fetches if the token string reference changes on re-render).
    const currentToken = localStorage.getItem('echo_session') ?? ''
    const currentHeaders = { Authorization: `Bearer ${currentToken}` }

    Promise.all([
      fetch(`/api/v1/projects/${projectId}`, { headers: currentHeaders })
        .then(r => r.ok ? r.json() as Promise<ProjectInfo> : null)
        .catch(() => null),
      fetch(`/api/v1/projects/${projectId}/uploads`, { headers: currentHeaders })
        .then(r => r.ok ? r.json() as Promise<UploadInfo[]> : [])
        .catch(() => []),
      fetch(`/api/v1/projects/${projectId}/twin-panel/readiness`, { headers: currentHeaders })
        .then(r => r.ok ? r.json() as Promise<ReadinessData> : null)
        .catch(() => null),
      fetch(`/api/v1/projects/${projectId}/derived-templates`, { headers: currentHeaders })
        .then(r => r.ok ? r.json() as Promise<{ templates: DerivedTemplate[] }> : { templates: [] })
        .catch(() => ({ templates: [] })),
      fetch(`/api/v1/projects/${projectId}/twin-panel/segments`, { headers: currentHeaders })
        .then(r => r.ok ? r.json() as Promise<{ segments: SegmentData[] }> : { segments: [] })
        .catch(() => ({ segments: [] })),
      fetch(`/api/v1/projects/${projectId}/twin-panel/bulk-queries?limit=5`, { headers: currentHeaders })
        .then(r => r.ok ? r.json() as Promise<{ queries: RecentResult[] }> : { queries: [] })
        .catch(() => ({ queries: [] })),
      fetch(`/api/v1/projects/${projectId}/twin-panel/baselines`, { headers: currentHeaders })
        .then(r => r.ok ? r.json() as Promise<BaselineData> : null)
        .catch(() => null),
    ]).then(([p, ups, r, t, s, q, b]) => {
      setProject(p)
      if (Array.isArray(ups) && ups.length > 0) setUpload(ups[0])
      if (r && r.total_twins > 0) setReadiness(r)
      setTemplates(t.templates ?? [])
      setSegments(s.segments ?? [])
      setRecentResults(q.queries ?? [])
      if (b) setBaselines(b)
    }).catch(() => {}).finally(() => setLoading(false))

    // Fetch research thread (separate — never blocks main load)
    fetch(`/app/projects/${projectId}/research-thread`, {
      headers: { Authorization: `Bearer ${currentToken}` },
    })
      .then(r => r.ok ? r.json() as Promise<ResearchThreadItem[]> : [])
      .then(data => setResearchThread(data))
      .catch(() => {})
  // refreshTick increments when pipeline status transitions to complete, triggering a re-fetch
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isDemo, refreshTick])

  // Pipeline-building poller: while no panel data has loaded yet, poll every 5s.
  // When the pipeline becomes complete/ready, increment refreshTick to trigger a full data re-fetch.
  // Stops automatically once twins/segments are loaded (avoids infinite refresh loops).
  const pipelinePollDoneRef = useRef(false)
  useEffect(() => {
    if (isDemo || !projectId) return
    // If we already have panel data (readiness or segments), no need to poll
    if (readiness !== null || segments.length > 0) {
      pipelinePollDoneRef.current = true
      return
    }
    if (pipelinePollDoneRef.current) return
    let timer: ReturnType<typeof setInterval> | null = null
    let active = true
    const checkPipeline = async () => {
      if (!active || pipelinePollDoneRef.current) return
      try {
        const currentToken = localStorage.getItem('echo_session') ?? ''
        const res = await fetch(`/api/v1/projects/${projectId}/pipeline/status`, {
          headers: { Authorization: `Bearer ${currentToken}` },
        })
        if (!res.ok) return
        const data = await res.json() as { status?: string }
        if (data.status === 'complete' || data.status === 'ready') {
          if (timer) clearInterval(timer)
          pipelinePollDoneRef.current = true
          if (active) setRefreshTick(t => t + 1)
        }
      } catch { /* ignore */ }
    }
    // Start polling after 2 seconds (give initial fetch a chance to load first)
    const initialDelay = setTimeout(() => {
      checkPipeline()
      timer = setInterval(checkPipeline, 5_000)
    }, 2_000)
    return () => {
      active = false
      clearTimeout(initialDelay)
      if (timer) clearInterval(timer)
    }
  }, [projectId, isDemo, readiness, segments.length])

  const pct = (n: number) => `${Math.round(n * 100)}%`

  async function runTemplateDirect(t: DerivedTemplate) {
    if (runningTemplateId) return
    setRunningTemplateId(t.template_id)
    try {
      const currentToken = localStorage.getItem('echo_session') ?? ''
      const res = await fetch(`/api/v1/projects/${projectId}/twin-panel/bulk-queries`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question_text: t.pre_filled_question }),
      })
      if (res.ok) {
        const data = await res.json() as { query_id?: string }
        if (data.query_id) {
          navigate(`/app/projects/${projectId}/twin-panel/queries/${data.query_id}`)
          return
        }
      }
    } catch (err) {
      console.error('run-template direct failed', err)
    }
    setRunningTemplateId(null)
  }

  if (loading) {
    return <div className="p-8 text-sm text-slate-400">Loading project…</div>
  }

  if (isDemo) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center space-y-4">
        <h1 className="text-2xl font-medium text-deep-ink">Demo workspace</h1>
        <p className="text-sm text-slate-500">You're in demo mode. Import your own survey data to get started.</p>
        <Link to="/app/upload">
          <Button variant="primary" size="lg">Import survey data</Button>
        </Link>
      </div>
    )
  }

  const hasUpload = !!upload
  const hasPanel = readiness && readiness.total_twins > 0
  const isPanelBuilding = !hasPanel && hasUpload

  // Panel status strip
  const panelStatus = !hasUpload
    ? { label: 'No data yet', color: 'text-slate-400', variant: 'default' as const }
    : isPanelBuilding
      ? { label: 'Building panel…', color: 'text-uncertainty-amber', variant: 'warning' as const }
      : readiness
        ? { label: `${readiness.twins_validated} twins validated`, color: 'text-validation-green', variant: 'success' as const }
        : { label: 'Ready', color: 'text-validation-green', variant: 'success' as const }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">

      {/* 1 — Status strip */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-medium text-deep-ink">
            {project?.name ?? `Project ${projectId?.slice(0, 8)}…`}
          </h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500 flex-wrap">
            <Badge variant={panelStatus.variant} size="sm">{panelStatus.label}</Badge>
            {upload && (
              <span>{upload.row_count?.toLocaleString()} respondents · {upload.filename}</span>
            )}
          </div>
        </div>
        <Link to={`/app/projects/${projectId}/upload`}>
          <Button variant="outline" size="sm" className="gap-1.5">
            <UploadCloud className="w-4 h-4" strokeWidth={1.75} />
            Import another wave
          </Button>
        </Link>
      </div>

      {/* 2 — "Your data, recognized" card */}
      {hasUpload && (
        <div data-testid="recognition-card">
        <Card className="bg-echo-indigo/5 border-echo-indigo/20">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="text-xs font-medium text-echo-indigo uppercase tracking-wide">Your data, recognized</p>
              <p className="text-sm font-medium text-deep-ink">{upload.filename}</p>
              <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                <span>{upload.row_count?.toLocaleString()} respondents</span>
                {segments.length > 0 && (
                  <span>
                    {segments.length} segment{segments.length > 1 ? 's' : ''} from{' '}
                    <span className="font-medium text-deep-ink">
                      {segments[0]?.source_column ?? 'demographic columns'}
                    </span>
                  </span>
                )}
              </div>
              {segments.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {segments.slice(0, 4).map(s => (
                    <span key={s.segment_id} data-testid={`segment-card-${s.segment_id}`} className="px-2 py-0.5 rounded-full bg-white border border-echo-indigo/20 text-xs text-echo-indigo">
                      {s.name} ({s.respondent_count})
                    </span>
                  ))}
                  {segments.length > 4 && (
                    <span className="px-2 py-0.5 text-xs text-slate-400">+{segments.length - 4} more</span>
                  )}
                </div>
              )}
            </div>
            <CheckCircle className="w-5 h-5 text-validation-green shrink-0 mt-0.5" strokeWidth={1.75} />
          </div>
        </Card>
        </div>
      )}

      {/* 3 — Ask your panel (primary CTA) */}
      <div className="space-y-4" data-testid="ask-panel-cta">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-deep-ink">Ask your panel</h2>
          <Link to={`/app/projects/${projectId}/ask`} className="text-sm text-echo-indigo hover:underline flex items-center gap-1">
            Open full composer
            <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.75} />
          </Link>
        </div>

        {templates.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="first-template-shown">
            {templates.slice(0, 3).map(t => (
              <Link
                key={t.template_id}
                to={`/app/projects/${projectId}/ask?tab=predict&q=${encodeURIComponent(t.pre_filled_question)}`}
                className="text-left block"
              >
                <Card
                  className={cn(
                    'cursor-pointer transition-colors h-full',
                    highlightTemplateId === t.template_id
                      ? `ring-2 ring-echo-indigo${pulseStopped ? '' : ' animate-pulse'}`
                      : 'hover:border-echo-indigo/30'
                  )}
                  data-testid={`template-card-${t.template_id}`}
                  data-highlighted={highlightTemplateId === t.template_id ? 'true' : undefined}
                >
                  <p className="text-sm font-medium text-deep-ink line-clamp-2">{t.name}</p>
                  <p className="text-xs text-slate-400 mt-1 line-clamp-2">{t.rationale}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-echo-indigo font-medium">
                    Ask this question →
                  </div>
                  <button
                    data-testid={`run-template-now-${t.template_id}`}
                    onClick={e => { e.preventDefault(); e.stopPropagation(); runTemplateDirect(t) }}
                    disabled={runningTemplateId === t.template_id}
                    aria-disabled={runningTemplateId === t.template_id}
                    className="mt-2 w-full text-xs font-medium text-white bg-echo-indigo rounded-lg px-3 py-1.5 hover:bg-echo-indigo/90 transition-colors"
                    style={runningTemplateId === t.template_id ? { opacity: 0.7, cursor: 'not-allowed' } : undefined}
                  >
                    {runningTemplateId === t.template_id ? 'Running…' : 'Run now'}
                  </button>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="border-dashed py-6 text-center">
            {hasPanel ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Your panel is ready. Ask it anything.</p>
                <Link to={`/app/projects/${projectId}/ask`}>
                  <Button variant="primary" size="md" className="gap-2">
                    <MessageSquare className="w-4 h-4" strokeWidth={1.75} />
                    Open Ask composer
                  </Button>
                </Link>
              </div>
            ) : isPanelBuilding ? (
              <div className="space-y-2">
                <div className="w-32 h-1.5 bg-soft-panel rounded-full mx-auto overflow-hidden">
                  <div className="h-full bg-echo-indigo rounded-full w-2/3 animate-pulse" />
                </div>
                <p className="text-sm text-slate-500">Building your panel — templates will appear when ready.</p>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-400">Import survey data to generate research questions tailored to your columns.</p>
                <Link to={`/app/projects/${projectId}/upload`}>
                  <Button variant="outline" size="sm" className="gap-1.5" data-testid="empty-state-cta">
                    <UploadCloud className="w-4 h-4" strokeWidth={1.75} />
                    Import survey data
                  </Button>
                </Link>
              </div>
            )}
          </Card>
        )}

        {/* Free-form ask */}
        <div
          className="flex items-center gap-2 px-4 py-2.5 border border-border-gray rounded-xl bg-white cursor-text hover:border-echo-indigo/40 transition-colors"
          onClick={() => navigate(`/app/projects/${projectId}/ask`)}
        >
          <MessageSquare className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={1.75} />
          <span className="text-sm text-slate-400 flex-1">Ask anything about your respondents…</span>
          <span className="text-xs text-slate-300">↵</span>
        </div>
      </div>

      {/* 4 — Trust panel */}
      {readiness && readiness.twins_validated > 0 && (
        <Card className="space-y-4" data-testid="trust-panel">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Panel accuracy</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-medium text-deep-ink" style={{ letterSpacing: '-0.03em' }}>
                  {pct(readiness.mean_accuracy)}
                </span>
                <span className="text-sm text-slate-400">mean · Wilson 95%: {pct(readiness.wilson_lower)}–{pct(readiness.wilson_upper)}</span>
              </div>
            </div>
            <div className="text-right space-y-1">
              <p className="text-xs text-slate-400">{readiness.twins_validated} validated</p>
              {readiness.twins_low_confidence > 0 && (
                <p className="text-xs text-uncertainty-amber">{readiness.twins_low_confidence} low confidence</p>
              )}
              {readiness.twins_excluded > 0 && (
                <p className="text-xs text-slate-400">{readiness.twins_excluded} excluded</p>
              )}
            </div>
          </div>

          {/* Baseline bars */}
          {baselines && (
            <div className="space-y-2 pt-2 border-t border-border-gray">
              {[
                { label: 'Random baseline', value: baselines.random_baseline },
                { label: 'Modal baseline', value: baselines.modal_baseline },
                { label: 'Echosome panel', value: baselines.echosome_accuracy, highlight: true },
              ].map(bar => (
                <div key={bar.label} className="flex items-center gap-3">
                  <span className="text-xs text-slate-500 w-28 shrink-0">{bar.label}</span>
                  <div className="flex-1 bg-soft-panel rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${bar.highlight ? 'bg-echo-indigo' : 'bg-slate-300'}`}
                      style={{ width: `${Math.round(bar.value * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-deep-ink w-8 text-right">{pct(bar.value)}</span>
                </div>
              ))}
              <p className="text-xs text-slate-400 pt-1">
                +{Math.round(baselines.lift_vs_modal * 100)} points above always-guessing-the-most-common-answer
              </p>
            </div>
          )}

          <div className="flex items-center gap-3 pt-1 border-t border-border-gray text-xs">
            <Link to={`/app/projects/${projectId}/twin-panel/accuracy`} className="text-echo-indigo hover:underline">
              Read calibration report
            </Link>
            <span className="text-slate-300">·</span>
            <Link to={`/app/projects/${projectId}/twin-panel/accuracy/misses`} className="text-echo-indigo hover:underline flex items-center gap-1" data-testid="misses-link">
              <AlertTriangle className="w-3 h-3" strokeWidth={1.75} />
              See where we were wrong
            </Link>
          </div>
        </Card>
      )}

      {/* 5 — Recent answers */}
      {recentResults.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-medium text-deep-ink">Recent answers</h2>
            <Link to={`/app/projects/${projectId}/twin-panel/queries`} className="text-sm text-echo-indigo hover:underline">
              All queries
            </Link>
          </div>
          <div className="space-y-2" data-testid="research-thread">
            {recentResults.slice(0, 5).map((r, idx) => (
              <Link
                key={r.id}
                data-testid={`thread-item-${idx}`}
                to={`/app/projects/${projectId}/twin-panel/queries/${r.id}`}
                className="block"
              >
                <Card className="hover:border-echo-indigo/30 transition-colors cursor-pointer py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm text-deep-ink truncate">{r.question}</p>
                      {r.headline && r.headline !== 'Analyzing your panel…' && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{r.headline}</p>
                      )}
                    </div>
                    <Badge variant={r.status === 'completed' ? 'success' : r.status === 'running' ? 'info' : 'warning'} size="sm">
                      {r.status}
                    </Badge>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* 6 — Research thread */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-medium text-deep-ink">Research thread</h2>
          <Link to={`/app/projects/${projectId}/ask`} className="text-sm text-echo-indigo hover:underline flex items-center gap-1">
            Open Ask
            <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.75} />
          </Link>
        </div>
        <div data-testid="research-thread" className="space-y-1">
          {researchThread.length === 0 ? (
            <p className="text-xs text-slate-400">No questions asked yet.</p>
          ) : (
            researchThread.slice(0, 5).map(item => (
              <Link
                key={item.thread_id}
                to={`/app/projects/${projectId}/ask?q=${encodeURIComponent(item.question_text)}&tab=${item.result_type}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border-gray hover:border-echo-indigo/40 hover:bg-echo-indigo/5 transition-colors"
              >
                <MessageSquare className="w-3.5 h-3.5 text-echo-indigo shrink-0" strokeWidth={1.75} />
                <span className="text-xs text-deep-ink truncate flex-1">{item.question_text}</span>
                <span className="text-xs text-slate-400 shrink-0">{item.result_type}</span>
              </Link>
            ))
          )}
        </div>
      </div>

      {/* 7 — Next-step rail */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-border-gray">
        {readiness && readiness.twins_low_confidence > 0 && (
          <Link to={`/app/projects/${projectId}/twin-panel`}>
            <Card className="hover:border-echo-indigo/30 cursor-pointer transition-colors py-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-uncertainty-amber shrink-0" strokeWidth={1.75} />
                <div>
                  <p className="text-xs font-medium text-deep-ink">Boost low-confidence</p>
                  <p className="text-xs text-slate-400">{readiness.twins_low_confidence} twins improvable</p>
                </div>
              </div>
            </Card>
          </Link>
        )}
        {readiness && readiness.twins_validated > 0 && recentResults.length > 0 && (
          <Link to={`/app/projects/${projectId}/report`}>
            <Card className="hover:border-echo-indigo/30 cursor-pointer transition-colors py-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-echo-indigo shrink-0" strokeWidth={1.75} />
                <div>
                  <p className="text-xs font-medium text-deep-ink">Generate client report</p>
                  <p className="text-xs text-slate-400">From your recent results</p>
                </div>
              </div>
            </Card>
          </Link>
        )}
        <Link to={`/app/projects/${projectId}/upload`}>
          <Card className="hover:border-echo-indigo/30 cursor-pointer transition-colors py-3">
            <div className="flex items-center gap-2">
              <UploadCloud className="w-4 h-4 text-echo-indigo shrink-0" strokeWidth={1.75} />
              <div>
                <p className="text-xs font-medium text-deep-ink">Import another wave</p>
                <p className="text-xs text-slate-400">Add more survey data</p>
              </div>
            </div>
          </Card>
        </Link>
        {readiness && readiness.twins_validated > 0 && (
          <Link to={`/app/projects/${projectId}/twin-panel/twins`}>
            <Card className="hover:border-echo-indigo/30 cursor-pointer transition-colors py-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-echo-indigo shrink-0" strokeWidth={1.75} />
                <div>
                  <p className="text-xs font-medium text-deep-ink">Inspect individual twins</p>
                  <p className="text-xs text-slate-400">{readiness.twins_validated} respondent echoes</p>
                </div>
              </div>
            </Card>
          </Link>
        )}
      </div>

      <p className="text-xs text-slate-400">
        Decision support only. Echosome surfaces communication review-risk signals. Final review stays with your legal, compliance, medical, or regulatory team.
      </p>
    </div>
  )
}
